import { db, META, type LocalHousehold, type MetaRow } from '@/db/db';
import { getCloudClient } from '@/auth/client';
import { purgeBaby, purgeBabyArchives } from './purge';
import { discardCloudWorkspace, privacyAction, type AccountSession } from '@/auth/account';

export interface PrivacyScope { householdId: string; userId: string | null; cloud: boolean }
function confirm(confirmed: boolean) { if (!confirmed) throw new Error('Explicit deletion confirmation is required.'); }
async function selected(scope: PrivacyScope, allowEmpty = false) {
  const h = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
  const s = (await db.meta.get(META.session))?.value as AccountSession | undefined;
  if (!h && allowEmpty && scope.householdId === '' && !scope.cloud && (s?.userId ?? null) === scope.userId) return null;
  if (!h || h.id !== scope.householdId || h.cloud !== scope.cloud || (s?.userId ?? null) !== scope.userId
    || (h.cloud && h.accountId !== scope.userId)) throw new Error('Workspace changed. Reopen this action.');
  return h;
}
async function remote(scope: PrivacyScope, name: string, args: Record<string, unknown>, owner: boolean) {
  if (!navigator.onLine) throw new Error('Connect to the internet to delete cloud data.');
  if (!scope.userId) throw new Error('Sign in first.');
  const client = getCloudClient();
  const user = await client.auth.getUser();
  if (user.error) throw user.error;
  if (user.data.user?.id !== scope.userId) throw new Error('Account changed. Sign in again.');
  await selected(scope, name === 'delete_my_account');
  if (owner || name === 'delete_my_account') {
    const members = await client.rpc('list_my_households');
    if (members.error) throw members.error;
    if (!Array.isArray(members.data)) throw new Error('Household permissions could not be verified. Try again.');
    const memberships = members.data as {id: string; name: string; role: string}[];
    const membership = memberships.find(h => h.id === scope.householdId);
    // A lost response may follow a successful household deletion. Let the
    // server's private retirement receipt authorize that exact retry.
    if (owner && membership && membership.role !== 'owner') throw new Error('Only the current household owner can delete cloud data.');
    const owned = memberships.filter(h => h.role === 'owner');
    if (name === 'delete_my_account' && owned.length) throw new Error(`Delete owned households first: ${owned.map(h => h.name).join(', ')}.`);
  }
  await selected(scope, name === 'delete_my_account');
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const request = client.rpc(name, args);
    const result = await Promise.race([
      typeof request.abortSignal === 'function' ? request.abortSignal(controller.signal) : request,
      new Promise<never>((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('Deletion response timed out. The server may have completed it. Reconnect and review before retrying.')); }, 20_000); }),
    ]);
    if (result.error) throw result.error;
    if (!result.data || result.data.status !== 'deleted') throw new Error('Deletion was not confirmed by the server. Your local records were preserved.');
  } finally { clearTimeout(timer); }
}

export async function deleteBabyData(scope: PrivacyScope, babyId: string, confirmed: boolean) {
  confirm(confirmed); const h = await selected(scope);
  if (!h) throw new Error('Select a household first.');
  const baby = await db.babies.get(babyId);
  if (!baby || baby.household_id !== h.id) throw new Error('Baby profile changed. Reopen this action.');
  await privacyAction(scope, h.cloud ? () => remote(scope, 'delete_baby_data', { p_household_id: h.id, p_baby_id: babyId }, true) : null,
    () => purgeBaby(db, h.id, babyId, h.cloud), h.cloud ? () => purgeBabyArchives(db, h.id, babyId, true) : undefined);
}

async function purgeHouseholdArchives(householdId: string) {
  for (const row of await db.meta.toArray()) if ((row.key.startsWith('cloudContext:') || row.key.startsWith('promotionRequest:')) && row.key.endsWith(`:${householdId}`)) await db.meta.delete(row.key);
}

export async function deleteHouseholdData(scope: PrivacyScope, confirmed: boolean) {
  confirm(confirmed); const h = await selected(scope);
  if (!h) throw new Error('Select a household first.');
  await privacyAction(scope, h.cloud ? () => remote(scope, 'delete_household', { p_household_id: h.id }, true) : null, async () => {
    if (h.cloud) {
      await purgeHouseholdArchives(h.id);
      await discardCloudWorkspace();
    } else {
      await Promise.all([db.events.clear(), db.babies.clear(), db.outbox.clear(), db.importBatches.clear(), db.predictionLog.clear()]);
      for (const row of await db.meta.toArray()) if (row.key !== META.deviceId && row.key !== META.session && row.key !== META.authGeneration && row.key !== 'privacyBusy' && !row.key.startsWith('cloudContext:')) await db.meta.delete(row.key);
    }
  }, h.cloud ? () => purgeHouseholdArchives(h.id) : undefined);
}

async function purgeAccountArchives(userId: string) {
  for (const row of await db.meta.toArray()) {
    if (row.key.startsWith(`cloudContext:${userId}:`) || row.key.startsWith(`promotionRequest:${userId}:`)) await db.meta.delete(row.key);
    else if (row.key === META.localContext) {
      const saved = row.value as { metadata?: MetaRow[] };
      if (saved?.metadata) await db.meta.put({ ...row, value: { ...saved, metadata: saved.metadata.filter(r => !r.key.startsWith(`promotionRequest:${userId}:`)) } });
    }
  }
}

/** Explicitly discard this account's cached cloud drafts; never touches remote records. */
export async function eraseAccountCache(scope: PrivacyScope, confirmed: boolean, deleteAccount = false) {
  confirm(confirmed); await selected(scope, true);
  if (!scope.userId) throw new Error('Sign in first.');
  const userId = scope.userId;
  await privacyAction(scope, deleteAccount ? () => remote(scope, 'delete_my_account', {}, false) : null, async () => {
    if (scope.cloud) await discardCloudWorkspace();
    await purgeAccountArchives(userId);
    await db.meta.delete(META.session);
  }, deleteAccount ? () => purgeAccountArchives(userId) : undefined);
  // Local scope invalidation is committed even if token cleanup fails.
  const result = await getCloudClient().auth.signOut({ scope: 'local' });
  if (result.error) throw new Error('Browser records erased. Sign-out token cleanup failed; close this browser and sign in again.');
}


